import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import {
    createValidationResult,
    FormDialog,
    FormFields,
    FormTextField,
    FormValidationSummary,
    ShimmeredDetailsList,
    useNotifications,
    usePerformMultipleActions,
    WarningMessage
} from "sprint-common-web";

import { useCloudFileApi } from "../../../../../../common/api/master-api/cloud-files/useCloudFileApi";
import { CourseOfActionFormFields } from "../../../../../common/course-of-action/CourseOfActionFormFields";
import { useCourseOfActionFormFieldsValues } from "../../../../../common/course-of-action/useCourseOfActionFormFieldsValues";
import { WaitingForClientResponseDetailsFormInputs } from "../../../../../common/waiting-for-client-response-form/WaitingForClientResponseDetailsForm";
import { CloudFileStatus, isInvalidStatusForWaitingForClientResponse } from "../../../../common/types/CloudFileStatus";
import { CloudFileListingContext } from "../../../CloudFileListingContext";
import { CloudFileListingItem } from "../../../types/CloudFileListingItem";
import {
    createCloudFileWaitingForClientResponseColumns,
    validateDiscardedCloudFilesOnWaitingForClientResponse
} from "./createCloudFileWaitingForClientResponseColumns";

type RequestMultipleCloudFilesWaitingForClientResponseDialogProps = {
    onClose: () => void;
};

export const RequestMultipleCloudFilesWaitingForClientResponseDialog = ({
    onClose
}: RequestMultipleCloudFilesWaitingForClientResponseDialogProps): JSX.Element => {
    const { success, error } = useNotifications();
    const { requestFilesWaitingForClientResponse } = useCloudFileApi();
    const { updateCloudFileStatus, selectedItems } = useContext(CloudFileListingContext);
    const { control, errors, handleSubmit, formState, reset } = useForm<WaitingForClientResponseDetailsFormInputs>();

    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<CloudFileListingItem>({
        isAvailable: (item) => !isInvalidStatusForWaitingForClientResponse(item.status),
        selectedItems
    });

    const {
        courseOfActions,
        fixedBillings,
        areCourseOfActionsLoading,
        areFixedBillingsLoading
    } = useCourseOfActionFormFieldsValues();

    const handleConfirm = async (inputs: WaitingForClientResponseDetailsFormInputs): Promise<void> => {
        const courseOfAction = courseOfActions.find((action) => action.id === inputs.courseOfActionId);
        const fixedBilling = inputs.fixedBillingId
            ? fixedBillings.find((billing) => billing.id === inputs.fixedBillingId)
            : undefined;

        const results = await performAllItems(async (item) => {
            await requestFilesWaitingForClientResponse({
                cloudFileId: item.id,
                courseOfActionId: inputs.courseOfActionId,
                fixedBillingId: inputs.fixedBillingId,
                details: inputs.details
            });

            updateCloudFileStatus(
                item.id,
                CloudFileStatus.WaitingForClientResponse,
                courseOfAction
                    ? {
                          courseOfAction: courseOfAction.name,
                          fixedBilling: fixedBilling?.name,
                          details: inputs.details
                      }
                    : undefined
            );
        });

        if (results.every(({ status }) => status === "fulfilled")) {
            onClose();

            success("Selected cloud files changed to Waiting for Client Response");
        } else {
            error("Changing to Waiting for Client Response failed");
        }
    };

    const handleClose = () => {
        onClose();
        reset();
    };

    const formHasErrors = createValidationResult([
        validateDiscardedCloudFilesOnWaitingForClientResponse(items, selectedItems.length)
    ]);

    const columns = createCloudFileWaitingForClientResponseColumns();

    const areValidCloudFilesEmpty = !items.length;

    return (
        <FormDialog
            title="Change cloud files status to Waiting for Client Response"
            formState={formState}
            text="Are you sure you want to change given cloud files to Waiting for Client Response?"
            isBlocking={isActionInProgress}
            showCloseButton={false}
            submit={{
                onClick: handleSubmit(handleConfirm),
                disabled:
                    areValidCloudFilesEmpty ||
                    isActionInProgress ||
                    areCourseOfActionsLoading ||
                    areFixedBillingsLoading ||
                    formState.isSubmitting ||
                    formState.isValidating
            }}
            cancel={{ onClick: handleClose }}
            width="65%"
            contentHeight={500}
        >
            {!areValidCloudFilesEmpty && (
                <FormFields>
                    <CourseOfActionFormFields
                        control={control}
                        errors={errors}
                        courseOfActions={courseOfActions}
                        fixedBillings={fixedBillings}
                    />
                    <FormTextField
                        control={control}
                        label="Status details"
                        name="details"
                        multiline={true}
                        errors={errors}
                    />
                </FormFields>
            )}
            {items.length > 0 && (
                <WarningMessage
                    message="If a file record is linked to the cloud file, it will also be updated to Waiting for Client Response."
                    styles={{ root: { marginTop: 10 } }}
                />
            )}
            <FormValidationSummary validationResult={formHasErrors} />
            {items.length > 0 && <ShimmeredDetailsList columns={columns} items={items} layoutMode="fixedColumns" />}
        </FormDialog>
    );
};
